import i18n, { t } from "i18next";

import { useSelector } from "react-redux";

import { getRiPercent } from "@components/OrderTaking/Helpers/calories";
import type { RootState } from "@components/OrderTaking/Store";
import { memoizedOrderEnergy as memoizedOrderEnergyRaw } from "@components/OrderTaking/Store/feature";

export type OrderEnergy = {
    visible: boolean;
    totalKcal: number;
    kcalText: string;
    ri: number;
};

// memoizedOrderEnergy is declared in a .js file, so reselect cannot infer its
// signature. Type it here once against RootState — the only place that bridges
// the JS slice to the TS consumers — instead of casting at every call site.
type OrderEnergySlice = {
    totalKcal: number;
    hasVisibleKcal: boolean;
    isCalorieActive: boolean;
};
const memoizedOrderEnergy = memoizedOrderEnergyRaw as unknown as (
    state: RootState
) => OrderEnergySlice;

export function useOrderEnergy(): OrderEnergy {
    // Single combined selector — fall back to safe defaults to avoid NaN
    // propagation when the store is not yet hydrated (cold restart with
    // redux-persist).
    const { totalKcal, hasVisibleKcal, isCalorieActive } =
        useSelector(memoizedOrderEnergy);

    const safeTotal = Number.isFinite(totalKcal) ? totalKcal : 0;
    const visible =
        Boolean(isCalorieActive) && Boolean(hasVisibleKcal) && safeTotal > 0;
    const ri = getRiPercent(safeTotal);
    const kcalText = `${Math.round(safeTotal).toLocaleString(
        i18n.language || undefined
    )} ${t("kcal")}`;

    return { visible, totalKcal: safeTotal, kcalText, ri };
}

export function useIsEnergyBannerVisible(): boolean {
    return useOrderEnergy().visible;
}
